import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { cn } from '@/lib/utils';
import type { CoverageRow, DomainBreakdownRow } from '../../types/activity-overview';

interface CoverageMatrixProps {
  rows: CoverageRow[];
  columnTotals: Array<{ domainId: number; count: number }>;
  grandTotal: number;
  domains: DomainBreakdownRow[];
}

/**
 * Coverage Matrix — communication stage × domain grid. Each cell is the number
 * of distinct activities with active content for that (stage, domain) under the
 * active status filter; row/column/grand totals are distinct-activity
 * aggregates (not cell sums). Zero cells are recommendation dead-zones and are
 * amber-highlighted. The cell tooltip also shows the raw active-stage
 * assignment count.
 */
export function CoverageMatrix({ rows, columnTotals, grandTotal, domains }: CoverageMatrixProps) {
  const totalByDomainId = new Map(columnTotals.map((col) => [col.domainId, col.count]));

  return (
    <Card className="bg-pine-card border-pine-line shadow-pine-card rounded-pine-card">
      <CardHeader>
        <CardTitle className="text-heading-4 text-pine-ink">Coverage Matrix</CardTitle>
      </CardHeader>
      <CardContent className="space-y-3">
        <div className="overflow-x-auto">
          <table className="w-full border-collapse text-sm">
            <thead>
              <tr>
                <th className="border border-pine-line bg-pine-hover-item px-3 py-2 text-left text-pine-ink font-semibold">
                  Stage \ Domain
                </th>
                {domains.map((domain) => (
                  <th
                    key={domain.id}
                    className="border border-pine-line bg-pine-hover-item px-3 py-2 text-left text-pine-ink font-semibold"
                  >
                    <span className="flex items-center gap-2">
                      <span className="inline-block h-2.5 w-2.5 shrink-0 rounded-full bg-pine-pine" />
                      {domain.title}
                    </span>
                  </th>
                ))}
                <th className="border border-pine-line bg-pine-hover-item px-3 py-2 text-right text-pine-ink font-semibold">
                  Total
                </th>
              </tr>
            </thead>
            <tbody>
              {rows.map((row) => (
                <tr key={row.stage.id}>
                  <th className="border border-pine-line bg-pine-hover-item px-3 py-2 text-left text-pine-ink font-semibold whitespace-nowrap">
                    {row.stage.title}
                  </th>
                  {row.cells.map((cell) => {
                    const isZero = cell.count === 0;
                    return (
                      <td
                        key={cell.domainId}
                        title={`${row.stage.title} × ${
                          domains.find((d) => d.id === cell.domainId)?.title ?? cell.domainId
                        }: ${cell.count} ${
                          cell.count === 1 ? 'activity' : 'activities'
                        } · ${cell.assignmentCount} active stage ${
                          cell.assignmentCount === 1 ? 'assignment' : 'assignments'
                        }`}
                        className={cn(
                          'border border-pine-line px-3 py-2 text-center tabular-nums',
                          isZero
                            ? 'bg-amber-100 text-amber-900 font-semibold'
                            : 'text-pine-ink'
                        )}
                      >
                        {cell.count}
                      </td>
                    );
                  })}
                  <td className="border border-pine-line px-3 py-2 text-right text-pine-ink font-semibold tabular-nums">
                    {row.rowTotal}
                  </td>
                </tr>
              ))}
            </tbody>
            <tfoot>
              <tr>
                <th className="border border-pine-line bg-pine-hover-item px-3 py-2 text-left text-pine-ink font-semibold">
                  Total
                </th>
                {domains.map((domain) => (
                  <td
                    key={domain.id}
                    className="border border-pine-line bg-pine-hover-item px-3 py-2 text-center text-pine-ink font-semibold tabular-nums"
                  >
                    {totalByDomainId.get(domain.id) ?? 0}
                  </td>
                ))}
                <td className="border border-pine-line bg-pine-hover-item px-3 py-2 text-right text-pine-ink font-semibold tabular-nums">
                  {grandTotal}
                </td>
              </tr>
            </tfoot>
          </table>
        </div>
        <p className="text-caption text-pine-ink-soft">
          Each cell is the number of distinct activities with active content for that stage ×
          domain; totals count distinct activities (not cell sums). Zero cells are recommendation
          dead-zones: a child at that communication stage with that domain focus has no content
          under the active status filter.
        </p>
      </CardContent>
    </Card>
  );
}
